/**
 * @file ordersSyncInfoConverter.js
 * @description Conversor tipado de Firestore para el documento `users/{storeId}` con
 * información de sincronización de órdenes.
 *
 * Con este conversor centralizamos la lógica de serialización y deserialización,
 * evitando "parches" y manteniendo un contrato de datos consistente. De esta manera
 * cumplimos el principio Single Responsibility (SRP) y otorgamos tipado fuerte a través
 * de JSDoc para que TypeScript detecte correctamente todas las propiedades.
 */

import { Timestamp } from 'firebase/firestore';

/**
 * @typedef {Object} OrdersSyncInfo
 * @property {Timestamp | Date | null | undefined} [ordersLastSync]  Fecha/hora de la última sincronización.
 * @property {string | undefined} [ordersSyncStatus]                 Estado de la sincronización (never, in_progress, completed, error).
 * @property {any} [ordersSyncDetails]                               Datos adicionales de la última sincronización.
 */

/** @type {import('firebase/firestore').FirestoreDataConverter<OrdersSyncInfo>} */
export const ordersSyncInfoConverter = {
  /**
   * Serializa los datos antes de enviarlos a Firestore.
   * @param {OrdersSyncInfo} data
   */
  toFirestore(data) {
    return {
      ordersLastSync: data.ordersLastSync ?? null,
      ordersSyncStatus: data.ordersSyncStatus ?? 'never',
      ordersSyncDetails: data.ordersSyncDetails ?? null,
    };
  },

  /**
   * Deserializa los datos recibidos desde Firestore.
   * @param {import('firebase/firestore').QueryDocumentSnapshot} snapshot
   * @param {import('firebase/firestore').SnapshotOptions} options
   * @returns {OrdersSyncInfo}
   */
  fromFirestore(snapshot, options) {
    const raw = snapshot.data(options);
    return {
      ordersLastSync: raw.ordersLastSync ?? null,
      ordersSyncStatus: raw.ordersSyncStatus ?? 'never',
      ordersSyncDetails: raw.ordersSyncDetails ?? null,
    };
  },
};
